import React, { useCallback } from "react";
import styled from "@emotion/styled";
/** @jsx jsx */
import { jsx, css, keyframes } from "@emotion/react";
import colors from "src/styles/variables.scss";
//Polyfill fill method

export enum ElementSize {
  SHORT = "SHORT",
  MEDIUM = "MEDIUM",
  LONG = "LONG",
}
interface BarConfig {
  height: string; //height of the skimmed element
  width: string; //width of the skimmed element
  backgroundColor?: string; //background color of the skimmed element.By default it is set to #2b2b33
  skimEffectColor?: string; //lighter color than background color to show skim effect. By default it is set to #4e4e57
  repeatBar?: number; //used to repeat individual skim elements .By-Default it is 1.
  elementSize?: string; //used to determine the approprite animation. By-Default it is short.
  style?: IObject<any>; // any style provided to each individual skim element
  className?: string; //className to be provided to the each individual skim element
}

interface IShimmer {
  barsConfig?: Array<BarConfig>; //configuration array involving  all the elements with appropriate properties
  style?: IObject<any>; //style the entire element stack
  repeatBarStack?: number; //used to repeat the entire barStack provided in the bar config.By-Default it is 1.
  className?: string; //className to be provided entire skim element stack
}

interface ISingleSkeleton {
  height: string;
  width: string;
  backgroundColor?: string;
  skimEffectColor?: string;
  elementSize?: string;
  style?: IObject<any>;
  className?: string;
}
const SingleSkeleton = styled.div<ISingleSkeleton>`
  width: ${({ width }) => width};
  display: inline-block;
  position: relative;
  height: ${({ height }) => height};
  background-repeat: no-repeat;
  background: ${({ backgroundColor }) => (backgroundColor ? backgroundColor : colors.bluishGray)};
  background-size: ${({ elementSize }) => {
    switch (elementSize) {
      case ElementSize.SHORT:
        return `250px 250px`;
      case ElementSize.MEDIUM:
        return `400px 400px`;
      case ElementSize.LONG:
        return `1000px 1000px`;
      default:
        return `250px 250px`;
    }
  }};
  background-image: ${({ backgroundColor = "#2b2b33", skimEffectColor }) => {
    return `linear-gradient(
    to right,
    ${backgroundColor} 0%,
    ${skimEffectColor ? skimEffectColor : colors.lightGray} 20%,
    ${backgroundColor} 40%,
    ${backgroundColor} 100%
  )`;
  }};
`;

const shortShimmer = keyframes`
            0% {
              background-position: -125px 0;
            }

            100% {
              background-position: 125px 0;
            }
          }
          `;

const mediumShimmer = keyframes`
            0% {
              background-position: -250px 0;
            }

            100% {
              background-position: 250px 0;
            }
          }
          `;

const longShimmer = keyframes`
            0% {
              background-position: -400px 0;
            }

            100% {
              background-position: 400px 0;
            }
          }
          `;

const defaultConfig = [{ width: "100%", height: "100%", elementSize: ElementSize.LONG }];

const Shimmer: React.FC<IShimmer> = ({ barsConfig = defaultConfig, style, repeatBarStack = 1, className }) => {
  const constructSingleBar = useCallback((singleBarConfig: BarConfig, key: number) => {
    singleBarConfig.elementSize = singleBarConfig.elementSize ? singleBarConfig.elementSize : ElementSize.SHORT;

    let shimEffect = {};
    switch (singleBarConfig.elementSize) {
      case ElementSize.SHORT:
        shimEffect = shortShimmer;
        break;
      case ElementSize.MEDIUM:
        shimEffect = mediumShimmer;
        break;
      case ElementSize.LONG:
        shimEffect = longShimmer;
        break;
      default:
        null;
    }

    const animatedCSS = css`
      animation: ${shimEffect} 1.75s linear infinite forwards;
    `;

    return (
      <SingleSkeleton
        height={singleBarConfig.height}
        width={singleBarConfig.width}
        backgroundColor={singleBarConfig.backgroundColor}
        skimEffectColor={singleBarConfig.skimEffectColor}
        elementSize={singleBarConfig.elementSize}
        style={singleBarConfig.style}
        key={key}
        css={animatedCSS}
        className={singleBarConfig.className}
      />
    );
  }, []);

  const constructBarStack = useCallback(
    (config, repetetions) => {
      try {
        //forming single stack of the entire based on the entire bar stack

        const repeatedArray = config.reduce((acc, eachBar) => {
          if (eachBar.repeatBar) {
            return acc.concat(Array(eachBar.repeatBar).fill(eachBar));
          }
          acc.push(eachBar);
          return acc;
        }, []);
        const finalArray = Array(repetetions).fill(repeatedArray).flat();

        return finalArray.map((eachBar, index) => constructSingleBar(eachBar, index), []);
      } catch (e) {
        // eslint-disable-next-line no-console
        throw new Error("Error forming shimmer");
      }
    },
    [barsConfig, repeatBarStack]
  );

  //group repeat of the bar stack formed earlier
  return (
    <div style={style} className={className}>
      {constructBarStack(barsConfig, repeatBarStack)}
    </div>
  );
};

export default Shimmer;
